import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useSnackbar } from 'notistack';

import ChannelMapView from './ChannelMapView';
import NeighborDataChipConnected from './NeighborDataChipConnected';
import { useOptimizeScope } from './OptimizeScopeContext';
import { NeighborDataIncompleteCard } from './NeighborDataStatus';
import { useAnalyserGate } from './useAnalyserGate';
import { useNeighborData } from './NeighborDataContext';
import { api } from '../../services/api';
import {
  DEFAULT_MIN_FREQ_GAP_MHZ,
  DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM,
  MAP_MARKER_RADIUS_M,
  filterOverlappingMarkers,
  getChannelColor,
} from '../../utils/channelMapUtils';
import {
  commitNumericDraft,
} from '../../utils/numericFieldUtils';
import {
  DEFAULT_CHANNEL_BANDWIDTH_MHZ,
  DEFAULT_SAME_CHASSIS_MIN_SEP_MHZ,
  buildChannelOptimizeRequest,
  buildChannelOptimizeViewModel,
  channelAnalysisScopeHelpText,
  describeChannelAnalyzeEmptyMap,
} from '../../utils/channelOptimizeViewUtils';
import {
  filterDevicesToScopeSerials,
  filterFreshnessToScope,
  filterMissingSerialsToScope,
  resolveOptimizeScopeSerials,
  scopeSummaryLabel,
} from '../../utils/optimizeScopeUtils';

const MIN_FREQ_GAP_MHZ = 1;

const ChannelsPanel = ({ devices, isActive }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { freshness, devices: neighborDevices, refresh: refreshDataStatus, scanDevices } = useNeighborData();
  const { setOptimizeScope } = useOptimizeScope();
  const { missingSerials, clearMissing, runGuarded, scanningSerials } = useAnalyserGate();

  const [customTrees, setCustomTrees] = useState([]);
  const [selectedCustomTrees, setSelectedCustomTrees] = useState([]);

  const [heardRssiFloorDbm, setHeardRssiFloorDbm] = useState(DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM);
  const [heardRssiFloorDraft, setHeardRssiFloorDraft] = useState(String(DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM));
  const [minFreqGapMHz, setMinFreqGapMHz] = useState(DEFAULT_MIN_FREQ_GAP_MHZ);
  const [minFreqGapDraft, setMinFreqGapDraft] = useState(String(DEFAULT_MIN_FREQ_GAP_MHZ));
  const [bandFilter, setBandFilter] = useState('6GHz');
  const [excludeIdleAPs, setExcludeIdleAPs] = useState(true);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [mapShowOverlappingOnly, setMapShowOverlappingOnly] = useState(true);

  const [loading, setLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [paramsDirty, setParamsDirty] = useState(false);
  const prevTreeScopeKeyRef = useRef(null);

  const markParamsDirty = useCallback(() => setParamsDirty(true), []);

  const selectedCustomTreeIds = useMemo(
    () => selectedCustomTrees.map((tree) => tree.id).filter(Boolean),
    [selectedCustomTrees],
  );
  const channelScopeIsSiteWide = selectedCustomTreeIds.length === 0;
  const scopeSerials = useMemo(
    () => resolveOptimizeScopeSerials('trees', {
      selectedCustomTrees,
      optimizeScopeIsSiteWide: channelScopeIsSiteWide,
    }),
    [selectedCustomTrees, channelScopeIsSiteWide],
  );
  const scopedDevices = useMemo(
    () => filterDevicesToScopeSerials(devices, scopeSerials),
    [devices, scopeSerials],
  );
  const scopeSummary = useMemo(
    () => scopeSummaryLabel('trees', { selectedCustomTrees }, []),
    [selectedCustomTrees],
  );
  const scopeHelpText = useMemo(
    () => channelAnalysisScopeHelpText(selectedCustomTrees),
    [selectedCustomTrees],
  );
  const gateMissingSerials = useMemo(() => {
    if (channelScopeIsSiteWide) return missingSerials;
    const scopedFreshness = filterFreshnessToScope(freshness, neighborDevices, scopeSerials);
    const scopedFromStatus = scopedFreshness?.missing_serials || [];
    if (scopedFromStatus.length > 0) return scopedFromStatus;
    return filterMissingSerialsToScope(missingSerials, scopeSerials);
  }, [
    channelScopeIsSiteWide,
    missingSerials,
    scopeSerials,
    freshness,
    neighborDevices,
  ]);

  const viewModel = useMemo(
    () => buildChannelOptimizeViewModel({
      optimizeResult,
      devices: scopedDevices,
      bandFilter,
      minFreqGapMHz,
    }),
    [optimizeResult, scopedDevices, bandFilter, minFreqGapMHz],
  );

  const {
    conflictCards,
    unsatisfiedCards,
    conflictIds,
    mapPlottableApRadios,
    bandApRadios,
    skippedNoApRadios,
    channelActions,
    planStatsText,
    score,
    noActionSummary,
    plannerIssues,
  } = viewModel;

  const overlapMapMarkers = useMemo(
    () => filterOverlappingMarkers(mapPlottableApRadios, conflictIds),
    [mapPlottableApRadios, conflictIds],
  );

  const mapMarkers = mapShowOverlappingOnly ? overlapMapMarkers : mapPlottableApRadios;

  const emptyMessage = useMemo(
    () => describeChannelAnalyzeEmptyMap({
      hasAnalysis: Boolean(optimizeResult),
      mapMarkerCount: mapMarkers.length,
      showOverlappingOnly: mapShowOverlappingOnly,
      hasAnalyzedApRadios: mapPlottableApRadios.length > 0,
    }),
    [optimizeResult, mapMarkers.length, mapShowOverlappingOnly, mapPlottableApRadios.length],
  );

  const legendMarkers = useMemo(
    () => [...mapMarkers].sort((a, b) => {
      if (a.band !== b.band) return a.band.localeCompare(b.band);
      if (a.channel !== b.channel) return a.channel - b.channel;
      return a.serial.localeCompare(b.serial);
    }),
    [mapMarkers],
  );

  const runChannelAnalysis = useCallback(async () => {
    setLoading(true);
    setAnalyzeError(null);
    const body = buildChannelOptimizeRequest({
      neighborRssiFloorDbm: heardRssiFloorDbm,
      minFrequencySeparationMHz: minFreqGapMHz,
      sameChassisMinSepMHz: DEFAULT_SAME_CHASSIS_MIN_SEP_MHZ,
      channelBandwidth: DEFAULT_CHANNEL_BANDWIDTH_MHZ,
      customTreeIds: selectedCustomTreeIds,
      excludeIdleAPs,
    });
    const outcome = await runGuarded(() => api.runOptimize(body));
    setLoading(false);
    if (outcome.ok) {
      clearMissing();
      setOptimizeResult(outcome.data);
      setParamsDirty(false);
      enqueueSnackbar('Channel analysis ready.', { variant: 'success' });
      return;
    }
    if (outcome.gated) {
      setOptimizeResult(null);
      enqueueSnackbar('Channel analysis blocked: neighbour data incomplete.', { variant: 'error' });
      return;
    }
    setOptimizeResult(null);
    setAnalyzeError(outcome.error?.message || 'Channel analysis failed');
    enqueueSnackbar(outcome.error?.message || 'Channel analysis failed', { variant: 'error' });
  }, [
    heardRssiFloorDbm,
    minFreqGapMHz,
    selectedCustomTreeIds,
    excludeIdleAPs,
    runGuarded,
    clearMissing,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    if (!isActive) return;
    setOptimizeScope({
      scopeSerials,
      scopeLabel: scopeSummary,
      isSiteWide: channelScopeIsSiteWide,
    });
  }, [isActive, scopeSerials, scopeSummary, channelScopeIsSiteWide, setOptimizeScope]);

  useEffect(() => {
    const treeScopeKey = selectedCustomTreeIds.join('|');
    if (prevTreeScopeKeyRef.current === null) {
      prevTreeScopeKeyRef.current = treeScopeKey;
      return;
    }
    if (prevTreeScopeKeyRef.current === treeScopeKey) return;
    prevTreeScopeKeyRef.current = treeScopeKey;
    clearMissing();
    setOptimizeResult(null);
    setAnalyzeError(null);
    setParamsDirty(true);
  }, [selectedCustomTreeIds, clearMissing]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getCustomTrees();
        if (cancelled) return;
        setCustomTrees(Array.isArray(data?.trees) ? data.trees : []);
      } catch {
        if (!cancelled) setCustomTrees([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleScanMissing = useCallback(async () => {
    if (!gateMissingSerials.length) return;
    const summary = await scanDevices(gateMissingSerials);
    if (!summary) return;
    enqueueSnackbar(
      summary.failed === 0
        ? `Triggered neighbour scan for ${summary.accepted} device(s).`
        : `Triggered ${summary.accepted} scan(s); ${summary.failed} failed.`,
      { variant: summary.failed === 0 ? 'info' : 'warning' },
    );
    refreshDataStatus();
  }, [gateMissingSerials, scanDevices, enqueueSnackbar, refreshDataStatus]);

  if (!isActive) return null;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <RouterIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Channels map
          </Typography>
        </Stack>
        <NeighborDataChipConnected />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Channel conflicts and proposed moves come from the optimize planner
        (<code>POST /tree-mgmt/analysis/optimize</code>, channel step only) — same graph,
        idle-AP rules, and heard-pair constraints as the Optimize tab. Analysis only;
        apply moves via the Optimize plan runner. Optionally scope to custom trees so
        only that cohort feeds the planner and map.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
          Analysis scope
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 320, flex: 1 }}
            options={customTrees}
            value={selectedCustomTrees}
            onChange={(_event, newValue) => {
              setSelectedCustomTrees(newValue);
            }}
            getOptionLabel={(option) => option.name || option.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Custom trees"
                placeholder={customTrees.length === 0 ? 'No segments defined' : 'All site (none selected)'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name || option.id}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{option.name || option.id}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.segment_id != null ? `Segment ${option.segment_id}` : 'No segment ID'}
                  </Typography>
                </Box>
              </Box>
            )}
          />
          {channelScopeIsSiteWide ? (
            <Chip color="default" variant="outlined" label="All Site" sx={{ mt: 0.5 }} />
          ) : (
            <Chip color="primary" variant="outlined" label={scopeSummary} sx={{ mt: 0.5 }} />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {scopeHelpText}
        </Typography>
      </Paper>

      {gateMissingSerials.length > 0 && (
        <NeighborDataIncompleteCard
          missingSerials={gateMissingSerials}
          onScanMissing={handleScanMissing}
          scanning={scanningSerials.length > 0}
          scanningSerials={scanningSerials}
          sx={{ mb: 2 }}
        />
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <Tooltip title={`Neighbour scan RSSI floor (dBm). Default ${DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM}. Passed to optimize as neighbor_rssi_floor_dbm.`}>
              <TextField
                label="Heard RSSI floor (dBm)"
                type="number"
                size="small"
                fullWidth
                value={heardRssiFloorDraft}
                onChange={(e) => {
                  setHeardRssiFloorDraft(e.target.value);
                  markParamsDirty();
                  const n = Number(e.target.value);
                  if (e.target.value !== '' && Number.isFinite(n)) {
                    setHeardRssiFloorDbm(n);
                  }
                }}
                onBlur={() => {
                  const committed = commitNumericDraft(heardRssiFloorDraft, {
                    min: -120,
                    fallback: DEFAULT_NEIGHBOR_RSSI_FLOOR_DBM,
                  });
                  setHeardRssiFloorDbm(committed);
                  setHeardRssiFloorDraft(String(committed));
                }}
                inputProps={{ max: 0, step: 1 }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Tooltip title={`Min center-frequency separation for heard pairs. Default ${DEFAULT_MIN_FREQ_GAP_MHZ} MHz.`}>
              <TextField
                label="Min frequency gap (MHz)"
                type="number"
                size="small"
                fullWidth
                value={minFreqGapDraft}
                onChange={(e) => {
                  setMinFreqGapDraft(e.target.value);
                  markParamsDirty();
                  const n = Number(e.target.value);
                  if (e.target.value !== '' && Number.isFinite(n) && n >= MIN_FREQ_GAP_MHZ) {
                    setMinFreqGapMHz(n);
                  }
                }}
                onBlur={() => {
                  const committed = commitNumericDraft(minFreqGapDraft, {
                    min: MIN_FREQ_GAP_MHZ,
                    fallback: DEFAULT_MIN_FREQ_GAP_MHZ,
                  });
                  setMinFreqGapMHz(committed);
                  setMinFreqGapDraft(String(committed));
                }}
                inputProps={{ min: MIN_FREQ_GAP_MHZ, step: 5 }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Band</InputLabel>
              <Select
                value={bandFilter}
                label="Band"
                onChange={(e) => {
                  setBandFilter(e.target.value);
                  markParamsDirty();
                }}
              >
                <MenuItem value="all">All backhaul</MenuItem>
                <MenuItem value="5GHz">5 GHz only</MenuItem>
                <MenuItem value="6GHz">6 GHz only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title="Default on: unused AP devices are excluded from heard/conflict planning. Turn off to keep all APs in the graph (idle_ap_retry_disabled)."
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={excludeIdleAPs}
                    onChange={(e) => {
                      setExcludeIdleAPs(e.target.checked);
                      markParamsDirty();
                    }}
                  />
                }
                label={<Typography variant="caption">Exclude unused APs</Typography>}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={runChannelAnalysis}
              disabled={loading || gateMissingSerials.length > 0}
              fullWidth
            >
              {loading ? 'Analyzing…' : paramsDirty ? 'Re-analyze' : 'Analyze channels'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ gap: 0.75 }}>
              <Chip size="small" label={`${bandApRadios.length} planner AP(s)`} color="primary" variant="outlined" />
              <Chip
                size="small"
                label={`${conflictCards.length} current issue(s)`}
                color={conflictCards.length > 0 ? 'error' : 'success'}
                variant={conflictCards.length > 0 ? 'filled' : 'outlined'}
              />
              {channelActions.length > 0 && (
                <Chip size="small" label={`${channelActions.length} proposed move(s)`} color="info" variant="outlined" />
              )}
              {mapPlottableApRadios.length > 0 && (
                <Chip size="small" label={`${mapPlottableApRadios.length} on map`} variant="outlined" />
              )}
              {paramsDirty && optimizeResult && (
                <Chip size="small" label="Settings changed" color="warning" variant="outlined" />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {analyzeError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {analyzeError}
        </Alert>
      )}

      {!loading && !optimizeResult && !analyzeError && gateMissingSerials.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Run channel analysis to load conflicts from the optimize planner.
        </Alert>
      )}

      {score?.summary_text && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {score.summary_text}
        </Alert>
      )}

      {planStatsText && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {planStatsText}
        </Typography>
      )}

      {noActionSummary && channelActions.length === 0 && plannerIssues.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {noActionSummary}
        </Alert>
      )}

      {skippedNoApRadios.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption" component="div">
            <strong>{skippedNoApRadios.length}</strong> device(s) have no detectable 5/6 GHz AP radios:{' '}
            {skippedNoApRadios.slice(0, 10).join(', ')}
            {skippedNoApRadios.length > 10 ? '…' : ''}
          </Typography>
        </Alert>
      )}

      <ChannelMapView
        markers={mapPlottableApRadios}
        conflictIds={conflictIds}
        markerRadiusM={MAP_MARKER_RADIUS_M}
        selectedMarkerId={selectedMarkerId}
        onSelectMarker={setSelectedMarkerId}
        emptyMessage={emptyMessage}
        hasAnalyzedApRadios={mapPlottableApRadios.length > 0}
        showOverlappingOnly={mapShowOverlappingOnly}
        onShowOverlappingOnlyChange={setMapShowOverlappingOnly}
      />

      {legendMarkers.length > 0 && (
        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {mapShowOverlappingOnly ? 'Conflicting APs on map' : 'APs on map'}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
            {legendMarkers.map((ap) => {
              const inConflict = conflictIds.has(ap.id);
              return (
                <Chip
                  key={ap.id}
                  size="small"
                  variant="outlined"
                  color={inConflict ? 'error' : 'default'}
                  label={`${ap.serialSuffix} · ${ap.band} ch${ap.channel}`}
                  onClick={() => setSelectedMarkerId(ap.id)}
                  sx={{
                    cursor: 'pointer',
                    borderColor: getChannelColor(ap.channel, ap.band, inConflict),
                  }}
                />
              );
            })}
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 1.5, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Issues on current channels ({conflictCards.length})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          What the planner sees <strong>before</strong> changing channels — overlap and heard-pair
          problems on today&apos;s live assignment (heard RSSI ≥ {heardRssiFloorDbm} dBm or same-chassis).
          If nothing appears under &ldquo;Planner unable to satisfy&rdquo;, proposed moves may fix these.
        </Typography>
        {!optimizeResult && !loading ? (
          <Alert severity="info">Run analysis to load current channel issues.</Alert>
        ) : conflictCards.length === 0 && !loading ? (
          <Alert severity="success">No overlap or heard-pair issues on current channels at these settings.</Alert>
        ) : (
          <Stack spacing={1} sx={{ maxHeight: 360, overflow: 'auto' }}>
            {conflictCards.map((pair) => (
              <Paper key={pair.pairKey || pair.id} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ gap: 0.75, mb: 0.75 }}>
                  <Chip size="small" color="primary" label="Current assignment" variant="outlined" />
                  {(pair.issueKinds || (pair.issueKind ? [pair.issueKind] : [])).map((kind) => (
                    <Chip key={kind} size="small" color="warning" label={kind} variant="outlined" />
                  ))}
                  {pair.severity && (
                    <Chip size="small" color="error" label={pair.severity} variant="outlined" />
                  )}
                  {pair.neighborEvidence === 'same_chassis' ? (
                    <Chip size="small" color="error" label="Same device" />
                  ) : pair.neighborEvidence === 'heard' ? (
                    <Chip
                      size="small"
                      color="error"
                      label={`RSSI ${pair.bestRssiDbm?.toFixed(0) ?? '—'} dBm`}
                    />
                  ) : null}
                  <Chip size="small" variant="outlined" label={`Δf ${Math.round(pair.freqSepMHz ?? 0)} MHz`} />
                  <Chip size="small" variant="outlined" label={`need ≥ ${pair.minGapMHz} MHz`} />
                  {pair.distanceM != null && pair.distanceM > 0 && (
                    <Tooltip title="GPS distance is informational — planner coupling uses neighbour scans.">
                      <Chip size="small" variant="outlined" label={`~${Math.round(pair.distanceM)} m apart`} />
                    </Tooltip>
                  )}
                  {pair.involvesCap && (
                    <Chip size="small" variant="outlined" label="Involves CAP" />
                  )}
                </Stack>
                {(pair.descriptions || (pair.description ? [pair.description] : [])).map((text) => (
                  <Typography key={text} variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {text}
                  </Typography>
                ))}
                <Typography variant="body2">
                  <strong>{pair.a.serial}</strong> ({pair.a.band} ch {pair.a.channel}, {pair.a.frequencyMHz} MHz)
                  {pair.a.boundToParent ? ' · pinned' : ''}
                  {' ↔ '}
                  <strong>{pair.b.serial}</strong> ({pair.b.band} ch {pair.b.channel}, {pair.b.frequencyMHz} MHz)
                  {pair.b.boundToParent ? ' · pinned' : ''}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {optimizeResult && !loading && unsatisfiedCards.length === 0 && conflictCards.length > 0 && (
        <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
          {channelActions.length > 0 ? (
            <>
              The planner found a better channel assignment ({channelActions.length} proposed move
              {channelActions.length === 1 ? '' : 's'} below). Open the <strong>Optimize</strong> tab,
              run optimize with the channel step, and apply the plan to put it on air.
            </>
          ) : (
            <>
              No pairs were left unsatisfied after the channel search — automatic re-channeling may
              still help. Open the <strong>Optimize</strong> tab, run optimize (channel step), and
              review whether to apply the proposed plan.
            </>
          )}
        </Alert>
      )}

      {unsatisfiedCards.length > 0 && (
        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Planner unable to satisfy ({unsatisfiedCards.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            After the channel search — pairs where the planner could not find a valid assignment
            under current pins, bandwidth, and spectrum rules. Manual changes may be required.
          </Typography>
          <Stack spacing={1} sx={{ maxHeight: 240, overflow: 'auto' }}>
            {unsatisfiedCards.map((pair) => (
              <Paper key={pair.id} variant="outlined" sx={{ p: 1.25, borderStyle: 'dashed' }}>
                <Chip size="small" color="error" label="After channel search" variant="outlined" sx={{ mb: 0.75 }} />
                <Typography variant="body2" color="text.secondary">
                  {pair.description || `${pair.a.serial} ↔ ${pair.b.serial}`}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      {channelActions.length > 0 && (
        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Proposed channel moves ({channelActions.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            From optimize <code>channel_actions</code>. Apply via the Optimize tab plan runner.
          </Typography>
          <Stack spacing={1}>
            {channelActions.map((action) => (
              <Paper key={action.id} variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="body2">
                  <strong>{action.serial}</strong>
                  {' · '}
                  {action.radio_name || action.band}
                  {' · '}
                  {action.groups_only
                    ? `lock ch ${action.proposed_channel}`
                    : `ch ${action.current_channel} → ${action.proposed_channel}`}
                  {action.groups_only && (
                    <Chip size="small" label="channel fix" color="info" sx={{ ml: 1 }} variant="outlined" />
                  )}
                  {action.proposed_htmode && (
                    <Chip size="small" label={action.proposed_htmode} sx={{ ml: 1 }} variant="outlined" />
                  )}
                  {Array.isArray(action.proposed_channel_group) && action.proposed_channel_group.length > 0 && (
                    <Chip
                      size="small"
                      label={`groups [${action.proposed_channel_group.join(', ')}]`}
                      sx={{ ml: 1 }}
                      variant="outlined"
                    />
                  )}
                  {!action.groups_only && action.improvement != null && (
                    <Chip
                      size="small"
                      label={`Δ severity ${Number(action.improvement).toFixed(2)}`}
                      sx={{ ml: 1 }}
                      variant="outlined"
                    />
                  )}
                </Typography>
                {action.description && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {action.description}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default ChannelsPanel;
